import { cn } from "@aurumiq/ui/lib/utils";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { ReactNode } from "react";

export interface SortableListProps<T> {
  items: T[];
  getId: (item: T) => string;
  onReorder: (next: T[]) => void;
  /** Row body; the drag handle is rendered by the list. */
  renderItem: (item: T, index: number) => ReactNode;
  rowClassName?: (item: T, index: number) => string | undefined;
  className?: string;
  ariaLabel?: string;
}

function Row({
  id,
  children,
  className,
  label,
}: {
  id: string;
  children: ReactNode;
  className?: string;
  label: string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex min-w-0 items-stretch gap-1 overflow-hidden rounded-lg border bg-card",
        isDragging && "z-10 shadow-lg",
        className,
      )}
    >
      <button
        type="button"
        className="flex cursor-grab touch-none items-center px-1 text-muted-foreground hover:text-foreground active:cursor-grabbing"
        aria-label={`Drag ${label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}

/** Vertical drag-to-reorder list (@dnd-kit) with keyboard support. */
export function SortableList<T>({
  items,
  getId,
  onReorder,
  renderItem,
  rowClassName,
  className,
  ariaLabel,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(getId);
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(items, from, to));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul className={cn("grid min-w-0 gap-1.5", className)} aria-label={ariaLabel}>
          {items.map((item, i) => (
            <Row
              key={ids[i]}
              id={ids[i] ?? String(i)}
              className={rowClassName?.(item, i)}
              label={ids[i] ?? String(i)}
            >
              {renderItem(item, i)}
            </Row>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
